import { useMutation } from "@apollo/client";
import { Draggable } from "@hello-pangea/dnd";
import { withErrorBoundary } from "@sentry/react";
import { Editor } from "@tiptap/core";
import { defer } from "lodash";
import {
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  NotesRequirement,
  TopicNodeNewPageFragmentFragment,
  UpdateTopicTitleDescriptionMutationMutation,
  UpdateTopicTitleDescriptionMutationMutationVariables,
} from "types/graphql-schema";

import TopicAssignee from "@apps/topic-assignee/topic-assignee";
import TopicCTA from "@apps/topic-cta/topic-cta";
import TopicDropdownMenu from "@apps/topic-dropdown-menu/topic-dropdown-menu";
import updateTopicMutation from "@apps/topic-sidebar/graphql/update-topic-title-description-mutation";
import { ButtonSize } from "@components/button/button-constants";
import Error from "@components/error/error";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, isEnterEvent } from "@helpers/helpers";

import {
  createTopicInMeeting,
  focusOnTopicTitleInput,
  topicHasSharedNotesContentToShow,
} from "../helpers";
import DiscussionNotes from "./discussion-notes";
import IndividualNotesContainer from "./individual-notes-container";

export const defaultTopicTitle = "New Topic";

const isCaretOnLastTextareaLine = (textarea: HTMLTextAreaElement) => {
  return !textarea.value.slice(textarea.selectionEnd).includes("\n");
};

const isCaretOnFirstTextareaLine = (textarea: HTMLTextAreaElement) => {
  return !textarea.value.slice(0, textarea.selectionStart).includes("\n");
};

const getEditableNoteEditors = (container: Element) => {
  return Array.from(
    container.querySelectorAll(".js-topic-discussion-notes-input"),
  ).filter((el) => {
    const editorEl = el as Element & { editor?: Editor };
    return editorEl.editor?.isEditable;
  }) as (Element & { editor: Editor })[];
};

const getVisibleEditableNoteEditors = (container: Element) => {
  return getEditableNoteEditors(container).filter(
    (el) => el.getClientRects().length > 0,
  );
};

const focusLastVisibleNoteEditor = (container: Element) => {
  const editorEls = getVisibleEditableNoteEditors(container);
  const lastActiveEditor = editorEls[editorEls.length - 1];
  if (lastActiveEditor?.editor) {
    lastActiveEditor.editor.chain().focus("end").run();
    return true;
  }
  return false;
};

const TopicContent = ({
  snapshot,
  provided,
  topic,
  meeting,
  opensInSidebar,
  isInExtension,
  isInPresentation,
  currentPageMeeting = null,
  index,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  index: number;
  opensInSidebar?: boolean;
  isInExtension?: boolean;
  isInPresentation?: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
  snapshot?: any;
  provided?: any;
}) => {
  const isSavingRef = useRef(false);
  const isFocusedRef = useRef(false);
  const listItemRef = useRef<HTMLLIElement | null>(null);
  const canEditTitle = topic.canUpdate.permission && !topic.isMandatory;
  const [editTitle, setEditTitle] = useState(
    topic.title === defaultTopicTitle ? "" : topic.title,
  );
  // Track whether the user has their focus anywhere inside this topic
  // (title, shared notes, or individual notes). Used to reveal empty
  // notes editors so the user can start typing.
  const [isInteracting, setIsInteracting] = useState(false);
  // Treat shared notes as "empty" only when both the prop (backend
  // snapshot) and the local editor agree — the cache.modify we do
  // elsewhere uses broadcast: false, so the prop can lag behind
  // keystrokes. A topic also counts as "not empty" when it has default
  // notes from an applied TF template to preview (linkedTemplateTopic) —
  // otherwise the shared notes section stays hidden (via `showSharedNotes`
  // below) until the user clicks into the topic, even though there's
  // template content ready to display. See topicHasSharedNotesContentToShow.
  const propSharedNotesIsEmpty = !topicHasSharedNotesContentToShow(topic);
  const [localSharedNotesIsEmpty, setLocalSharedNotesIsEmpty] = useState(
    propSharedNotesIsEmpty,
  );
  const sharedNotesIsTrulyEmpty =
    propSharedNotesIsEmpty && localSharedNotesIsEmpty;

  // Sync local state when topic title changes externally (e.g. via pusher)
  // but only if the user is not currently editing the input.
  useEffect(() => {
    if (!isFocusedRef.current) {
      setEditTitle(topic.title === defaultTopicTitle ? "" : topic.title);
    }
  }, [topic.title]);
  const isPlaceholderTitle = editTitle === defaultTopicTitle;
  const isTemporaryTopic =
    topic.id < 0 && String(topic.id) === String(topic.clientId);

  const [saveTopic] = useMutation<
    UpdateTopicTitleDescriptionMutationMutation,
    UpdateTopicTitleDescriptionMutationMutationVariables
  >(updateTopicMutation, {});

  const handleSave = useCallback(() => {
    if (editTitle.trim() === "") {
      if (topic.title !== defaultTopicTitle) {
        setEditTitle(topic.title);
      }
      return;
    }
    if (isSavingRef.current || !canEditTitle || isTemporaryTopic)
      return Promise.resolve();
    isSavingRef.current = true;
    const trimmed = editTitle.trim();
    let savePromise = Promise.resolve();
    if (trimmed && trimmed !== topic.title) {
      savePromise = saveTopic({
        variables: {
          topicId: topic.id,
          title: editTitle,
        },
        optimisticResponse: {
          __typename: "Mutation",
          createOrUpdateTopic: {
            topic: { ...topic, title: editTitle },
            __typename: "CreateOrUpdateTopicMutation",
          },
        },
      }).then(() => {});
    } else {
      setEditTitle(topic.title);
    }
    requestAnimationFrame(() => {
      isSavingRef.current = false;
    });
    return savePromise;
  }, [editTitle, canEditTitle, topic, isTemporaryTopic, saveTopic]);

  const handleCreateTopicBelow = useCallback(() => {
    createTopicInMeeting({
      meeting,
      afterTopicId: topic.id,
    });
  }, [meeting, topic.id]);

  const focusFirstNoteEditor = useCallback(() => {
    const container = document.querySelector(
      `#meeting-${meeting.id}-topic-${topic.id}`,
    );
    if (!container) return false;
    const editorEls = getVisibleEditableNoteEditors(container);
    const firstActiveEditor = editorEls[0];
    if (firstActiveEditor?.editor) {
      firstActiveEditor.editor.chain().focus("end").run();
      return true;
    }
    return false;
  }, [meeting.id, topic.id]);

  const focusPreviousTopicLastNoteEditor = useCallback(() => {
    const currentContainer = document.querySelector(
      `#meeting-${meeting.id}-topic-${topic.id}`,
    );
    if (!currentContainer) return false;
    const topicContainers = Array.from(
      document.querySelectorAll(`[id^="meeting-${meeting.id}-topic-"]`),
    );
    const currentIndex = topicContainers.indexOf(currentContainer);
    if (currentIndex <= 0) return false;
    const previousTopicContainer = topicContainers[currentIndex - 1];
    if (focusLastVisibleNoteEditor(previousTopicContainer)) {
      return true;
    }
    if (getEditableNoteEditors(previousTopicContainer).length === 0) {
      return false;
    }
    previousTopicContainer.dispatchEvent(
      new CustomEvent("topicflow:focus-last-note-editor"),
    );
    return true;
  }, [meeting.id, topic.id]);

  const setListItemRef = useCallback(
    (node: HTMLLIElement | null) => {
      listItemRef.current = node;
      provided?.innerRef?.(node);
    },
    [provided],
  );

  const focusLastNoteEditor = useCallback(() => {
    if (!listItemRef.current) return false;
    return focusLastVisibleNoteEditor(listItemRef.current);
  }, []);

  useEffect(() => {
    const node = listItemRef.current;
    if (!node) return;
    const handleFocusLastNoteEditor = () => {
      setIsInteracting(true);
      defer(focusLastNoteEditor);
    };
    node.addEventListener(
      "topicflow:focus-last-note-editor",
      handleFocusLastNoteEditor,
    );
    return () => {
      node.removeEventListener(
        "topicflow:focus-last-note-editor",
        handleFocusLastNoteEditor,
      );
    };
  }, [focusLastNoteEditor]);

  // When the user inserts shared / individual notes via the dropdown
  // "Insert ..." menu, keep the section visible until focus first lands
  // inside the topic — without this guard, the existing "hide when empty
  // and not interacting" logic would collapse the section the moment the
  // menu closes, before the user has a chance to type. Once focus enters
  // the topic the flag is cleared (in handleFocus), so deleting the text
  // and clicking outside hides the section as expected.
  const [sharedNotesInsertedThisSession, setSharedNotesInsertedThisSession] =
    useState(false);
  const [
    individualNotesInsertedThisSession,
    setIndividualNotesInsertedThisSession,
  ] = useState(false);
  const prevIncludesSharedNotesRef = useRef(topic.includesSharedNotes);
  const prevIncludesIndividualForCurrentUserRef = useRef(
    topic.includesIndividualNotesForCurrentUser,
  );
  useEffect(() => {
    if (!prevIncludesSharedNotesRef.current && topic.includesSharedNotes) {
      setSharedNotesInsertedThisSession(true);
    }
    if (
      !prevIncludesIndividualForCurrentUserRef.current &&
      topic.includesIndividualNotesForCurrentUser
    ) {
      setIndividualNotesInsertedThisSession(true);
    }
    prevIncludesSharedNotesRef.current = topic.includesSharedNotes;
    prevIncludesIndividualForCurrentUserRef.current =
      topic.includesIndividualNotesForCurrentUser;
  }, [topic.includesSharedNotes, topic.includesIndividualNotesForCurrentUser]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEnterEvent(e)) {
      e.preventDefault();
      handleCreateTopicBelow();
    } else if (e.key === "Tab" && !e.shiftKey) {
      // Move focus from the title to the first available notes editor
      // (shared notes if enabled, otherwise the current user's individual
      // notes).
      if (focusFirstNoteEditor()) {
        e.preventDefault();
      }
    } else if (
      e.key === "ArrowDown" &&
      isCaretOnLastTextareaLine(e.currentTarget)
    ) {
      if (focusFirstNoteEditor()) {
        e.preventDefault();
      }
    } else if (
      e.key === "ArrowUp" &&
      isCaretOnFirstTextareaLine(e.currentTarget)
    ) {
      if (focusPreviousTopicLastNoteEditor()) {
        e.preventDefault();
      }
    } else if (e.key === "Escape") {
      setEditTitle(topic.title === defaultTopicTitle ? "" : topic.title);
    }
  };

  let notesHelperText = "";
  if (topic.notesRequirement === NotesRequirement.OneParticipant) {
    notesHelperText = "At least one participant must add notes.";
  } else if (topic.notesRequirement === NotesRequirement.AllParticipants) {
    notesHelperText = "All participants must add notes.";
  }

  // if user has added a topic, it must have a temporary topic id
  // we'll use that to detect new topic and automatically focus on it.
  useEffect(() => {
    if (isTemporaryTopic && topic.title === defaultTopicTitle) {
      defer(() => {
        focusOnTopicTitleInput(topic.id, meeting.id);
      });
    }
  }, [isTemporaryTopic, meeting.id, topic.id, topic.title]);

  const hasEnabledIndividualNotes = assertEdgesNonNull(
    topic.individualNotes,
  ).some((individualNote) => individualNote.enabled);

  const individualNotesEnabled =
    topic.includesIndividualNotes ||
    topic.includesIndividualNotesForCurrentUser ||
    hasEnabledIndividualNotes;

  // Track websocket connection messages from each editor in the topic so we
  // can surface a single warning icon in the header (replaces the per-editor
  // "Trying to connect" banner).
  const [editorMessages, setEditorMessages] = useState<Record<string, string>>(
    {},
  );
  const handleSharedNotesMessage = useCallback((message: string | null) => {
    setEditorMessages((prev) => {
      const next = { ...prev };
      if (message === null) {
        if (!("shared" in next)) return prev;
        delete next.shared;
      } else {
        if (next.shared === message) return prev;
        next.shared = message;
      }
      return next;
    });
  }, []);
  const handleIndividualNoteMessage = useCallback(
    (key: string, message: string | null) => {
      setEditorMessages((prev) => {
        const next = { ...prev };
        if (message === null) {
          if (!(key in next)) return prev;
          delete next[key];
        } else {
          if (next[key] === message) return prev;
          next[key] = message;
        }
        return next;
      });
    },
    [],
  );
  const activeWebsocketMessage = Object.values(editorMessages)[0] || null;

  // Show shared notes when the topic has them turned on AND either the
  // user has focus inside the topic (so empty-state placeholder shows
  // for them to start typing), there's already content to display, or
  // the user has just inserted them via the dropdown this session.
  const showSharedNotes =
    topic.includesSharedNotes &&
    (isInteracting ||
      !sharedNotesIsTrulyEmpty ||
      sharedNotesInsertedThisSession);

  const handleFocus = useCallback(() => {
    setIsInteracting(true);
    // Once focus has reached the topic, the post-insert "stay visible"
    // guard is no longer needed. After this point, the standard
    // "hide when empty and not interacting" logic applies — so if the
    // user types, deletes, then clicks outside, the section will hide.
    setSharedNotesInsertedThisSession(false);
    setIndividualNotesInsertedThisSession(false);
  }, []);
  const handleBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setIsInteracting(false);
    }
  }, []);

  const isInFullScreen = index === undefined;
  return (
    <li
      key={topic.clientId}
      id={`meeting-${meeting.id}-topic-${topic.id}`}
      className={classNames(
        "group/topic relative",
        // scrollIntoView (from mark-as-discussed) should land the topic just
        // below the sticky meeting header (~80px tall — the date line plus the
        // meeting title beneath it) so the divider line above the topic sits at
        // the top of the viewport, rather than leaving the just-completed
        // topic's header peeking in above it.
        "scroll-mt-20",
        topic.isMandatory && "js-mandatory-topic",
        snapshot?.isDragging && "shadow-xl rounded-md",
      )}
      {...(provided?.draggableProps || {})}
      ref={setListItemRef}
      aria-label={`Topic ${topic.title}`}
      data-testid={`container-topic-${topic.id}`}
    >
      {/* DRAG HANDLE (left gutter, outside the card)
          Wrap the visible icon in a wider hover/hit area that butts up
          against the card so the icon stays visible as the cursor crosses
          the gap to click it. */}
      {!isInFullScreen && !isInExtension && (
        <div
          className={classNames(
            "absolute -left-7 top-6 w-7 flex items-start pt-0.5 cursor-grab",
            snapshot?.isDragging && "cursor-grabbing",
          )}
          {...(provided?.dragHandleProps || {})}
          aria-label="Topic drag handle"
        >
          <span
            className={classNames(
              "text-gray-400 hover:bg-gray-100 rounded-sm p-0.5",
              "invisible group-hover/topic:visible",
              snapshot?.isDragging && "visible",
            )}
          >
            <TopicflowIcon type="drag" className="h-4 w-4" />
          </span>
        </div>
      )}

      {/* CARD */}
      <div
        className={classNames(snapshot?.isDragging && "bg-purple-50/90")}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {/* Top padding — clicking here focuses the topic title. */}
        <div
          className="h-6 cursor-text"
          onClick={() => focusOnTopicTitleInput(topic.id, meeting.id)}
          aria-hidden
        />

        {/* TITLE + SHARED NOTES */}
        <div>
          {/* HEADER: title + actions */}
          <div className="flex w-full relative items-start gap-1">
            {/* TITLE */}
            <div className="flex-1 min-w-0">
              <TextareaAutosize
                key={topic.clientId}
                minRows={1}
                readOnly={!canEditTitle}
                className={classNames(
                  "block w-full p-0! border-0! m-0 leading-6 focus:outline-hidden focus:ring-0 resize-none bg-transparent",
                  "tf-header-sm-dark text-base",
                  isInPresentation && "text-xl",
                  canEditTitle ? "cursor-text" : "cursor-default",
                  isPlaceholderTitle && "text-gray-300!",
                  "js-meeting-topic-input",
                )}
                disabled={!canEditTitle}
                value={editTitle}
                placeholder="Enter a topic name..."
                onChange={(e) => {
                  setEditTitle(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  isFocusedRef.current = true;
                  // Reveal empty shared/individual notes sections (with
                  // placeholders) as soon as the title is focused.
                  setIsInteracting(true);
                }}
                onBlur={() => {
                  isFocusedRef.current = false;
                  handleSave();
                }}
                aria-label={"Topic title"}
              />
            </div>

            {/* ACTIONS */}
            {!isInExtension && (
              <div className="flex items-start gap-1">
                {topic.isMandatory && (
                  <Tooltip
                    text={`The creator of this template has marked the topic as mandatory and it cannot be edited. ${notesHelperText}`}
                  >
                    <div className="flex mt-1 invisible group-hover/topic:visible group-focus-within/topic:visible">
                      <TopicflowIcon
                        type="asterisk"
                        className="h-4 w-4 text-amber-600"
                      />
                    </div>
                  </Tooltip>
                )}
                {activeWebsocketMessage && (
                  <Tooltip text={activeWebsocketMessage}>
                    <div className="flex mt-1" aria-label="Connection issue">
                      <TopicflowIcon
                        type="warning"
                        className="h-4 w-4 text-amber-600"
                      />
                    </div>
                  </Tooltip>
                )}
                <div className="invisible group-hover/topic:visible group-focus-within/topic:visible">
                  <TopicAssignee
                    topic={topic}
                    size={ButtonSize.medium}
                    assignableUsers={meeting.participants?.edges.map(
                      (edge) => edge?.node?.user,
                    )}
                  />
                </div>
                <div className="invisible group-hover/topic:visible group-focus-within/topic:visible">
                  <TopicDropdownMenu
                    meetingGroup={meeting.meetingGroup}
                    topic={topic}
                    meeting={meeting}
                    portal={opensInSidebar}
                    currentPageMeeting={currentPageMeeting}
                    isLastTopic={meeting.topics?.edges.length === 1}
                    size={ButtonSize.medium}
                  />
                </div>
                <TopicCTA topic={topic} />
              </div>
            )}
          </div>

          {/* SHARED NOTES
              Keep the editor mounted whenever shared notes are enabled so the
              Yjs doc can sync and we can detect remote content even when the
              GraphQL snapshot (`topic.discussionNotes`) is stale or null.
              When the section should be collapsed, hide it visually with
              `hidden` instead of unmounting. */}
          {topic.includesSharedNotes && (
            <div className={classNames("mt-2.5", !showSharedNotes && "hidden")}>
              <DiscussionNotes
                topic={topic}
                meeting={meeting}
                className={isInPresentation ? "text-lg" : ""}
                onModEnter={handleCreateTopicBelow}
                onEnter={handleCreateTopicBelow}
                onEmptyChange={setLocalSharedNotesIsEmpty}
                onWebsocketMessageChange={handleSharedNotesMessage}
              />
            </div>
          )}
        </div>

        {/* INDIVIDUAL NOTES */}
        {individualNotesEnabled && (
          <IndividualNotesContainer
            topic={topic}
            meeting={meeting}
            onModEnter={handleCreateTopicBelow}
            isTopicInteracting={
              isInteracting || individualNotesInsertedThisSession
            }
            onWebsocketMessageChange={handleIndividualNoteMessage}
          />
        )}

        {/* Bottom padding — clicking here focuses the first available
            notes editor (shared if enabled, otherwise individual). */}
        <div
          className="h-6 cursor-text"
          onClick={() => {
            setIsInteracting(true);
            defer(focusFirstNoteEditor);
          }}
          aria-hidden
        />
      </div>
    </li>
  );
};

const Topic = ({
  topic,
  index,
  meeting,
  opensInSidebar,
  isInExtension,
  isInPresentation,
  currentPageMeeting = null,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  index: number;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  opensInSidebar?: boolean;
  isInExtension?: boolean;
  isInPresentation?: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
}) => {
  const isInFullScreen = index === undefined;

  return isInFullScreen ? (
    <TopicContent
      key={topic.clientId}
      topic={topic}
      index={index}
      meeting={meeting}
      opensInSidebar={opensInSidebar}
      isInExtension={isInExtension}
      isInPresentation={isInPresentation}
      currentPageMeeting={currentPageMeeting}
    />
  ) : (
    <Draggable draggableId={`topic-${topic.id}`} index={index}>
      {(provided, snapshot) => (
        <TopicContent
          provided={provided}
          snapshot={snapshot}
          isInPresentation={isInPresentation}
          key={topic.clientId}
          topic={topic}
          index={index}
          meeting={meeting}
          opensInSidebar={opensInSidebar}
          isInExtension={isInExtension}
          currentPageMeeting={currentPageMeeting}
        />
      )}
    </Draggable>
  );
};

const TopicWithSentry = withErrorBoundary(Topic, {
  fallback: <Error description={"The topic could not be rendered."} />,
});

export default TopicWithSentry;
